import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { useAuth } from 'contexts/AuthContext';
import { useWhiteLabel } from 'contexts/WhiteLabelContext';

const BrandContext = createContext(null);

/**
 * Owns the user's brand-access list and the currently-selected brand.
 *
 * Lives at the top of the React tree so the IAM list endpoint only
 * fires once per session — previously each top-level dashboard section
 * (`/dashboard/brand/*`, `/dashboard/webpage/*`, …) wrapped its own
 * AdminLayout, and React Router would remount the layout when you
 * crossed sections, re-firing the fetch each time.
 */
export function BrandProvider({ children }) {
  const { isAuthenticated } = useAuth();
  // On a brand's own domain there is exactly one brand and it is not the
  // person's to change. See WhiteLabelContext.
  const whiteLabel = useWhiteLabel();
  const [brandList, setBrandList] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [loaded, setLoaded] = useState(false);
  // Distinct from "no brands". A failed request used to be indistinguishable
  // from an empty account, so the dashboard offered to create a first brand
  // to someone who already had several — and the only clue was a toast that
  // disappeared after a few seconds.
  const [loadError, setLoadError] = useState(null);
  // Coalesces in-flight + initial double-fire (React StrictMode mounts
  // every effect twice in dev). Without this guard the IAM endpoint
  // gets two simultaneous hits at startup and the second one trips the
  // global rate limiter.
  const inflight = useRef(null);
  // A brand to switch to once the list comes back — set by whoever just
  // created one. It has to live outside the request because of the coalescing
  // above: `fetchBrands(newBrandId)` called while a plain `fetchBrands()` is
  // already running used to return that earlier promise and throw the id away,
  // leaving the user on their previous brand right after creating a new one.
  const preferred = useRef(null);

  const fetchBrands = useCallback((preferredId) => {
    if (preferredId) preferred.current = preferredId;
    if (inflight.current) return inflight.current;
    const p = API.getBrandsFromAccessListbyUserId()
      .then((res) => {
        const list = Array.isArray(res) ? res : (res?.data ?? res?.brands ?? []);
        setLoadError(null);
        setBrandList(list);
        const wanted = preferred.current;
        preferred.current = null;
        // An explicit request wins over whatever was selected before; only
        // fall back to the first brand when nothing is selected at all.
        /**
         * On a brand's own domain the host chooses, not the person.
         *
         * Picking "whatever was selected last" would mean someone who used
         * the platform dashboard this morning arrives at bakery.com/dashboard
         * looking at the bike shop — on the bakery's domain, under the
         * bakery's name.
         */
        if (whiteLabel.active && whiteLabel.brandId) {
          setSelectedBrand(whiteLabel.brandId);
        } else {
          setSelectedBrand((prev) => wanted || prev || list[0]?.brand_id?._id || null);
        }
        return list;
      })
      .catch((err) => {
        const message = err?.message || String(err);
        toast.error(message, { toastId: 'brand-list-error' });
        setLoadError(message);
        return [];
      })
      .finally(() => {
        // Mark "loaded" so the dashboard renders an empty state instead
        // of staying stuck waiting for an IAM call that failed.
        setLoaded(true);
        inflight.current = null;
        // Don't strand a pending preference on a failed request — the next
        // fetch would apply it long after the user moved on.
        preferred.current = null;
      });
    inflight.current = p;
    return p;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whiteLabel.active, whiteLabel.brandId]);

  // First fetch fires once when the user becomes authenticated.
  useEffect(() => {
    if (!whiteLabel.ready) return;
    if (isAuthenticated && !loaded && !inflight.current) fetchBrands();
    // `whiteLabel.ready` gates the first fetch: until the host is resolved we
    // do not know which brand to select, and selecting the wrong one first
    // shows the brand's staff a different brand's data for a moment.
  }, [isAuthenticated, loaded, fetchBrands, whiteLabel.ready]);

  return (
    <BrandContext.Provider
      value={{
        brandList,
        selectedBrand,
        setSelectedBrand,
        fetchBrands,
        loaded,
        loadError,
        retry: () => { setLoaded(false); setLoadError(null); },
      }}
    >
      {children}
    </BrandContext.Provider>
  );
}

export function useBrand() {
  const ctx = useContext(BrandContext);
  if (!ctx) throw new Error('useBrand must be used inside BrandProvider');
  return ctx;
}
